import { type PortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Security — `/organization/security` (plan-014 C2; design §27.15.1; FR12-SEC-04/05/06). Drawn to
 * the prototype's `orgsec` page (`design/WeTalk.dc.html`, the Organization block renderer): kicker ("… · applies to
 * everyone in the organization"), "Sign-in and access", the lede, the tab strip, then four blocks —
 *
 *   How people sign in   the three-way choice (Any method · Require an authenticator · Single sign-on only); picking
 *                        one shows its lockout preflight and a Save, never a silent change
 *   Before you enforce   the preflight in words: how many Owners and Admins already use an authenticator, the
 *   this (notice)        recovery codes an Owner holds, who is affected — or, refused, why no Owner would get in
 *   Verified domains     each domain with its state (Verified · Waiting for DNS · Lapsed), the TXT record to publish,
 *                        Verify now, the auto-join (a limited role in named workspaces) and Remove; "Add a domain"
 *   Allowed networks     the ranges with who added them and Remove; "Add a range"; the address WeTalk sees for this
 *                        browser — from its own proxy, never a header the browser wrote
 *
 * EVERY AFFORDANCE IS REAL (FR12-SCR-04) and offered only to an Owner or Admin (`account.sign_in_policy.write`); a
 * change answered `step_up_required` opens "Confirm it is you" (password, authenticator or recovery code) and is
 * retried once. Styles: the shared renderer's (`../page/organization-page.css`) plus `./account-security.css`, tokens
 * only (ADR 0018).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Dialog, Field, Input, Select } from "@wetalk/ui";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import { authContextQuery } from "../../../api/query.js";
import type {
  AccountSecurityView,
  AutoJoinRoleView,
  SignInPolicyKindView,
  SignInPolicyPreflightView,
  VerifiedDomainView,
} from "../../../api/slice/account-security.js";
import type { AccountMemberView } from "../../../api/slice/account-team.js";
import { GLYPH } from "../../../shell/glyph.js";
import { accountProfileKey, roleHolds, useOpenAccount } from "../overview/account-overview-screen.js";
import { Block, BlockNote, ItemColumn, ItemRow, Notice, OrganizationPage, type Outcome } from "../page/organization-page.js";
import { WORKSPACE_ROLE_WORD, shortDateText } from "../page/role-copy.js";
import { WorkspaceChoiceStatus, openWorkspaceOf, organizationWorkspaceReadOf, type OrganizationWorkspaceRead } from "../page/workspace-choice.js";

import { ConfirmIdentityDialog, isStepUpRequired, stepUpReturnOf, stepUpReturnOutcome, type PendingConfirmation } from "./confirm-identity.js";

import "./account-security.css";

/** The page's search parameters: `step_up` only on the way back from a Google or Microsoft confirmation. */
export interface AccountSecuritySearch {
  readonly step_up?: "confirmed" | "cancelled" | "failed";
}

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL still opens a
 * valid page.
 */
export function accountSecuritySearchOf(raw: Readonly<Record<string, unknown>>): AccountSecuritySearch {
  return stepUpReturnOf(raw);
}

export interface AccountSecurityScreenProps {
  readonly search: AccountSecuritySearch;
  readonly onSearch: (next: AccountSecuritySearch) => void;
}

export const accountSecurityKey = (accountId: string) => ["account-security", accountId] as const;
const memberKey = (accountId: string) => ["account-team", "members", accountId] as const;

/** The prototype's three options, in its order, with its words. */
const POLICY_OPTION: readonly { readonly kind: SignInPolicyKindView; readonly label: string; readonly dek: string }[] = [
  { kind: "any_method", get label() { return portalText("core", "account_admin.any_method_37089e"); }, get dek() { return portalText("core", "account_admin.password_google_or_microsoft_authenticator_optional_5654ce"); } },
  {
    kind: "authenticator_required",
    get label() { return portalText("core", "account_admin.require_an_authenticator_81718c"); },
    get dek() { return portalText("core", "account_admin.everyone_adds_a_second_step_applies_the_next_time_each_person_sig_c86270"); },
  },
  {
    kind: "single_sign_on_only",
    get label() { return portalText("core", "account_admin.single_sign_on_only_395281"); },
    get dek() { return portalText("core", "account_admin.google_or_microsoft_with_an_address_on_a_verified_domain_password_ab96ad"); },
  },
];

const AUTO_JOIN_ROLE: readonly AutoJoinRoleView[] = ["viewer", "member", "developer", "campaign_manager", "billing"];

const DOMAIN_STATE_WORD: Readonly<Record<VerifiedDomainView["state"], string>> = {
  get verified() { return portalText("core", "start.verified_aed3b8"); },
  get pending() { return portalText("core", "account_admin.waiting_for_dns_882e37"); },
  get lapsed() { return portalText("core", "account_admin.lapsed_47d928"); },
};

/** "checked 4 min ago" — how long since a moment, in the prototype's words. */
export function agoText(iso: string, now: Date): string {
  const minute = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 60_000));
  if (minute < 1) return portalText("core", "account_admin.just_now_a7f8e7");
  if (minute < 60) return portalText("core", "account_admin._min_ago_b343dd", { value0: String(minute) });
  const hour = Math.round(minute / 60);
  if (hour < 24) return hour === 1 ? portalText("core", "account_admin.1_hour_ago_ce7276") : portalText("core", "account_admin._hours_ago_fa0104", { value0: String(hour) });
  return shortDateText(iso);
}

/** The "Before you enforce this" sentence for one preflight. */
export function preflightText(preflight: SignInPolicyPreflightView, ownerName: string | null): string {
  const who = ownerName ?? portalText("core", "auth.an_owner_d8d838");
  if (preflight.kind === "single_sign_on_only") {
    return preflight.ownerRecoveryReady
      ? portalText("core", "account_admin._of_owners_already_sign_in_with_single_sign_on_on_a_verified_doma_53c813", { value0: String(preflight.ownerRecoveryCount), value1: String(preflight.ownerCount) })
      : portalText("core", "account_admin.no_owner_signs_in_with_google_or_microsoft_on_a_verified_domain_y_352c9c");
  }
  const share = portalText("core", "account_admin._of_owners_and_admins_already_use_an_authenticator_31aa5d", { value0: String(preflight.administratorWithAuthenticatorCount), value1: String(preflight.administratorCount) });
  if (preflight.kind === "any_method") return portalText("core", "account_admin._nobody_is_challenged_until_their_next_sign_in_40c92c", { value0: share });
  if (!preflight.ownerRecoveryReady) {
    return portalText("core", "account_admin._no_owner_has_an_authenticator_with_recovery_codes_yet_so_this_co_0023e8", { value0: share });
  }
  const codes = preflight.ownerRecoveryCodeLeast ?? 0;
  return portalText("core", "account_admin.recovery_safety", { share, who, count: codes });
}

const REASON_WORD: Readonly<Record<SignInPolicyPreflightView["affectedPerson"][number]["reason"], string>> = {
  get add_authenticator() { return portalText("core", "account_admin.will_be_asked_to_add_an_authenticator_63a4ae"); },
  get no_recovery_code() { return portalText("core", "account_admin.has_no_recovery_codes_left_8649e8"); },
  get loses_access() { return portalText("core", "account_admin.would_lose_access_886821"); },
};

/** The people a choice affects, in one sentence. */
export function affectedText(preflight: SignInPolicyPreflightView): string | null {
  if (preflight.affectedPerson.length === 0) return null;
  const named = preflight.affectedPerson.slice(0, 3).map((one) => portalText("core", "account_admin._843a2d", { value0: one.displayName ?? one.email, value1: REASON_WORD[one.reason] }));
  const more = preflight.affectedPerson.length - named.length;
  return `${named.join("; ")}${more > 0 ? portalText("core", "account_admin._and_more_b7a3e0", { value0: String(more) }) : ""}.`;
}

/** What a domain row says under its name. */
function domainDek(domain: VerifiedDomainView): string {
  if (domain.state === "verified") {
    return domain.autoJoin === null
      ? portalText("core", "account_admin.joining_without_an_invitation_is_off_cd02d6")
      : portalText("core", "account_admin.join_as_in_0943db", { value0: WORKSPACE_ROLE_WORD[domain.autoJoin.memberRole], value1: domain.autoJoin.workspace.map((one) => one.name).join(", ") });
  }
  const shortValue = domain.txtValue.length > 36 ? `${domain.txtValue.slice(0, 32)}…` : domain.txtValue;
  if (domain.state === "lapsed") return portalText("core", "account_admin.txt_was_missing_on_the_daily_check_single_sign_on_stays_enforced_5619ae", { value0: shortValue });
  return domain.lastCheckOutcome === "not_found_yet" ? portalText("core", "account_admin.txt_not_found_yet_c84ad7", { value0: shortValue }) : portalText("core", "account_admin.add_txt_at_499060", { value0: shortValue, value1: domain.txtName });
}

function domainMeta(domain: VerifiedDomainView, now: Date): string | undefined {
  if (domain.state === "verified" && domain.verifiedAt !== null) return portalText("core", "account_admin.verified_00d4e8", { value0: shortDateText(domain.verifiedAt) });
  if (domain.state === "lapsed" && domain.lapsedAt !== null) return portalText("core", "account_admin.lapsed_d2cf06", { value0: shortDateText(domain.lapsedAt) });
  if (domain.lastCheckedAt !== null) return portalText("core", "account_admin.checked_2875ed", { value0: agoText(domain.lastCheckedAt, now) });
  return undefined;
}

// ── the page ──────────────────────────────────────────────────────────────────────────────────────────────────

export function AccountSecurityScreen(props: AccountSecurityScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const open = useOpenAccount();
  const accountId = open.accountId;
  const canRead = roleHolds(open.accountRole, "account.security.read");
  const canWrite = roleHolds(open.accountRole, "account.sign_in_policy.write");

  const security = useQuery({
    queryKey: accountSecurityKey(accountId ?? "none"),
    queryFn: () => gateway.readAccountSecurity(accountId ?? ""),
    enabled: accountId !== null && canRead,
  });
  const profile = useQuery({
    queryKey: accountProfileKey(accountId ?? "none"),
    queryFn: () => gateway.readAccountProfile(accountId ?? ""),
    enabled: accountId !== null && canRead,
  });
  const member = useQuery({
    queryKey: memberKey(accountId ?? "none"),
    queryFn: () => gateway.readAccountMembers(accountId ?? ""),
    enabled: accountId !== null && canRead,
  });

  // A Google or Microsoft confirmation's return (`?step_up=`) is shown once, then dropped from the address.
  const [outcome, setOutcome] = useState<Outcome | null>(() => stepUpReturnOutcome(props.search.step_up));
  const { onSearch } = props;
  const returned = props.search.step_up;
  useEffect(() => {
    if (returned !== undefined) onSearch({});
  }, [returned, onSearch]);
  const [pending, setPending] = useState<PendingConfirmation | null>(null);

  /** Runs a change; `step_up_required` opens the confirmation and the change is retried once after it. */
  const change = async (act: () => Promise<PortalMessage>): Promise<void> => {
    const finish = async (): Promise<void> => {
      const text = await act();
      setOutcome({ tone: "done", text });
      await queryClient.invalidateQueries({ queryKey: ["account-security"] });
    };
    try {
      await finish();
    } catch (raised) {
      if (isStepUpRequired(raised)) {
        setPending({ retry: finish });
        return;
      }
      setOutcome({ tone: "problem", get text() { return apiProblemText(raised, portalText("core", "account_admin.that_change_could_not_be_made_0e975e")); } });
    }
  };

  const selfPersonId = useSelfPersonId();
  const self = member.data?.find((one) => one.personId === selfPersonId);
  const page = (children: ReactNode): ReactNode => (
    <OrganizationPage
      tab="security"
      title={portalText("core", "account_admin.sign_in_and_access_f1ac8f")}
      lede={portalText("core", "account_admin.these_rules_are_checked_at_sign_in_when_someone_accepts_an_invita_6e4d71")}
      outcome={outcome}
    >
      {children}
      <ConfirmIdentityDialog
        pending={pending}
        preferred={self === undefined || self.signIn.password ? "password" : "totp"}
        returnPath="/organization/security"
        onDone={() => setPending(null)}
        onProblem={(text) => setOutcome({ tone: "problem", text })}
      />
    </OrganizationPage>
  );

  if (open.loading) return page(<BlockNote>{portalText("core", "account_admin.loading_the_organization_c14ce2")}</BlockNote>);
  if (accountId === null) return page(<BlockNote>{portalText("core", "account_admin.choose_an_organization_first_066988")}</BlockNote>);
  if (!canRead) return page(<BlockNote>{portalText("core", "account_admin.only_an_owner_or_an_admin_of_the_organization_sees_its_sign_in_ru_6ce0ee")}</BlockNote>);
  if (security.isPending) return page(<BlockNote>{portalText("core", "account_admin.loading_the_sign_in_rules_7212ed")}</BlockNote>);
  if (security.isError) return page(<BlockNote>{apiProblemText(security.error, portalText("core", "account_admin.the_sign_in_rules_could_not_be_loaded_531484"))}</BlockNote>);

  const view = security.data;
  const owner = member.data?.find((one) => one.accountRole === "owner");
  return page(
    <>
      <PolicyBlock
        view={view}
        canWrite={canWrite}
        ownerName={owner?.displayName ?? null}
        onSave={(kind) =>
          change(async () => {
            const saved = await gateway.saveAccountSignInPolicy(accountId, kind, view.policyRevision);
            if (saved.state === "draft") return () => (portalText("core", "account_admin.saved_as_a_draft_verify_a_domain_first_or_those_people_lose_acces_5b8457"));
            if (saved.unchanged) return () => (portalText("core", "account_admin.that_is_already_how_people_sign_in_df3e15"));
            return () => (kind === "any_method"
              ? portalText("core", "account_admin.policy_saved_nobody_is_challenged_until_their_next_sign_in_7930a0")
              : kind === "authenticator_required"
                ? portalText("core", "account_admin.policy_saved_anyone_without_an_authenticator_is_asked_to_add_one_c3050f")
                : portalText("core", "account_admin.policy_saved_passwords_stop_working_for_addresses_on_your_verifie_145a2c"));
          })
        }
      />
      <DomainBlock
        view={view}
        canWrite={canWrite}
        workspace={organizationWorkspaceReadOf(profile)}
        accountId={accountId}
        change={change}
      />
      <NetworkBlock view={view} canWrite={canWrite} member={member.data ?? []} accountId={accountId} change={change} />
    </>,
  );
}

/** The signed-in person's id (the auth context names them). */
function useSelfPersonId(): string | null {
  const gateway = useGateway();
  const context = useQuery(authContextQuery(gateway));
  return context.data?.person.personId ?? null;
}

// ── How people sign in ────────────────────────────────────────────────────────────────────────────────────────

function PolicyBlock(props: {
  readonly view: AccountSecurityView;
  readonly canWrite: boolean;
  readonly ownerName: string | null;
  readonly onSave: (kind: SignInPolicyKindView) => Promise<void>;
}): ReactNode {
  usePortalTranslation("core");
  const enforced: SignInPolicyKindView = props.view.policy?.kind ?? "any_method";
  const [chosen, setChosen] = useState<SignInPolicyKindView>(enforced);
  const [saving, setSaving] = useState(false);
  const preflight = props.view.preflight[chosen];
  const affected = affectedText(preflight);
  const unverified = chosen === "single_sign_on_only" && !props.view.verifiedDomain.some((one) => one.state === "verified");
  return (
    <>
      <Block title={portalText("core", "account_admin.how_people_sign_in_7fc92d")}>
        <div className="sec-choice" role="radiogroup" aria-label={portalText("core", "account_admin.how_people_sign_in_7fc92d")}>
          {POLICY_OPTION.map((option) => (
            <label key={option.kind} className={option.kind === chosen ? "sec-option sec-option-on" : "sec-option"}>
              <input
                type="radio"
                name="sign-in-policy"
                className="sec-option-radio"
                checked={option.kind === chosen}
                disabled={!props.canWrite}
                onChange={() => setChosen(option.kind)}
              />
              <span className="sec-option-body">
                <span className="sec-option-label">
                  {option.label}
                  {option.kind === enforced ? <span className="tag tag-accent sec-option-tag">{portalText("core", "account_admin.in_force_377013")}</span> : null}
                </span>
                <span className="sec-option-dek">{option.dek}</span>
              </span>
            </label>
          ))}
        </div>
        {props.view.draft === null ? null : (
          <p className="sec-line">{portalText("core", "account_admin.a_draft_is_saved_49e55a")}{" "}{POLICY_OPTION.find((one) => one.kind === props.view.draft?.kind)?.label ?? portalText("core", "account_admin.single_sign_on_only_8e08a3")}{portalText("core", "account_admin._it_applies_once_a_domain_is_verified_and_you_save_it_again_764727")}</p>
        )}
        {props.canWrite && chosen !== enforced ? (
          <div className="sec-actions">
            <Button
              variant="primary"
              disabled={saving}
              onClick={() => {
                setSaving(true);
                void props.onSave(chosen).finally(() => setSaving(false));
              }}
            >
              {unverified ? portalText("core", "account_admin.save_as_a_draft_459bd1") : portalText("core", "account_admin.save_efc007")}
            </Button>
          </div>
        ) : null}
      </Block>
      <Block>
        <Notice
          tone={chosen === "any_method" ? "accent" : "caution"}
          glyph={GLYPH.lockKey}
          title={portalText("core", "account_admin.before_you_enforce_this_83bc19")}
          text={[preflightText(preflight, props.ownerName), affected].filter((one) => one !== null).join(" ")}
        />
      </Block>
    </>
  );
}

// ── Verified domains ──────────────────────────────────────────────────────────────────────────────────────────

function DomainBlock(props: {
  readonly view: AccountSecurityView;
  readonly canWrite: boolean;
  readonly workspace: OrganizationWorkspaceRead;
  readonly accountId: string;
  readonly change: (act: () => Promise<PortalMessage>) => Promise<void>;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<VerifiedDomainView | null>(null);
  const now = new Date();
  return (
    <Block
      title={portalText("core", "account_admin.verified_domains_6ea08b")}
      dek={portalText("core", "account_admin.people_with_an_address_on_a_verified_domain_can_join_without_an_i_cdaa8b")}
      buttons={
        props.canWrite ? (
          <Button variant="secondary" onClick={() => setAdding(true)}>{portalText("core", "account_admin.add_a_domain_b4b41f")}</Button>
        ) : undefined
      }
    >
      {props.view.verifiedDomain.length === 0 ? (
        <BlockNote>{portalText("core", "account_admin.no_domain_yet_add_one_publish_the_txt_record_we_show_you_then_pre_546dfb")}</BlockNote>
      ) : (
        <ItemColumn label={portalText("core", "account_admin.verified_domains_6ea08b")}>
          {props.view.verifiedDomain.map((domain) => (
            <ItemRow
              key={domain.ssoDomainId}
              glyph={GLYPH.globe}
              tone={domain.state === "verified" ? "accent" : "neutral"}
              title={domain.domain}
              tag={DOMAIN_STATE_WORD[domain.state]}
              dek={domainDek(domain)}
              {...(domainMeta(domain, now) === undefined ? {} : { meta: domainMeta(domain, now) as string })}
              actions={
                props.canWrite ? (
                  <span className="sec-row-actions">
                    {domain.state === "verified" ? (
                      <Button variant="secondary" onClick={() => setEditing(domain)}>{portalText("core", "account_admin.edit_530164")}</Button>
                    ) : (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          void props.change(async () => {
                            try {
                              const checked = await gateway.verifyAccountVerifiedDomain(props.accountId, domain.ssoDomainId);
                              return () => (checked.outcome === "found" ? portalText("core", "account_admin._is_verified_160848", { value0: domain.domain }) : portalText("core", "account_admin.still_not_found_dns_can_take_up_to_an_hour_056848"));
                            } catch (raised) {
                              if (raised instanceof ApiError && raised.code === "domain_check_unavailable") {
                                return () => (portalText("core", "account_admin.we_could_not_reach_dns_just_now_nothing_changed_try_again_in_a_fe_bc5395"));
                              }
                              throw raised;
                            }
                          })
                        }
                      >{portalText("core", "account_admin.verify_now_dbf641")}</Button>
                    )}
                    <Button
                      variant="secondary"
                      onClick={() =>
                        void props.change(async () => {
                          await gateway.removeAccountVerifiedDomain(props.accountId, domain.ssoDomainId);
                          return () => (portalText("core", "account_admin._was_removed_2b28ec", { value0: domain.domain }));
                        })
                      }
                    >{portalText("core", "account_admin.remove_e96390")}</Button>
                  </span>
                ) : undefined
              }
            />
          ))}
        </ItemColumn>
      )}
      <AddDomainDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdd={(name) =>
          props.change(async () => {
            const added = await gateway.addAccountVerifiedDomain(props.accountId, name);
            setAdding(false);
            return () => (portalText("core", "account_admin.add_txt_at_then_press_verify_now_0bc8ce", { value0: added.txtValue, value1: added.txtName }));
          })
        }
      />
      <AutoJoinDialog
        domain={editing}
        workspace={props.workspace}
        onClose={() => setEditing(null)}
        onSave={(role, workspace) =>
          props.change(async () => {
            if (editing === null) return () => (portalText("core", "account_admin.nothing_changed_632fb8"));
            await gateway.setAccountVerifiedDomainAutoJoin(props.accountId, editing.ssoDomainId, role, workspace);
            setEditing(null);
            return () => (role === null ? portalText("core", "account_admin.joining_without_an_invitation_is_off_81dee1") : portalText("core", "account_admin.saved_9d4af6"));
          })
        }
      />
    </Block>
  );
}

function AddDomainDialog(props: { readonly open: boolean; readonly onClose: () => void; readonly onAdd: (domain: string) => Promise<void> }): ReactNode {
  usePortalTranslation("core");
  const [domain, setDomain] = useState("");
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (domain.trim() === "") return;
    void props.onAdd(domain.trim()).then(() => setDomain(""));
  };
  return (
    <Dialog open={props.open} title={portalText("core", "account_admin.add_a_domain_b4b41f")} onClose={props.onClose}>
      <form className="sec-dialog-form" onSubmit={submit}>
        <p className="sec-dialog-text">{portalText("core", "account_admin.we_show_you_a_txt_record_to_add_to_the_domain_s_dns_once_it_is_pu_84e06d")}</p>
        <Field label={portalText("core", "account_admin.domain_9b1091")}>
          {(control) => <Input {...control} value={domain} placeholder={portalText("core", "account_admin.romapizzeria_com_a1dbb1")} onChange={(event) => setDomain(event.currentTarget.value)} />}
        </Field>
        <div className="sec-dialog-actions">
          <Button variant="primary" type="submit" disabled={domain.trim() === ""}>{portalText("core", "account_admin.add_61cc55")}</Button>
        </div>
      </form>
    </Dialog>
  );
}

function AutoJoinDialog(props: {
  readonly domain: VerifiedDomainView | null;
  /** The organization's open workspaces, as its profile read stands (loading, failed or ready). */
  readonly workspace: OrganizationWorkspaceRead;
  readonly onClose: () => void;
  readonly onSave: (role: AutoJoinRoleView | null, workspace: readonly string[]) => Promise<void>;
}): ReactNode {
  usePortalTranslation("core");
  const current = props.domain?.autoJoin ?? null;
  const [role, setRole] = useState<AutoJoinRoleView | "off">(current?.memberRole ?? "off");
  const [workspace, setWorkspace] = useState<string>(current?.workspace[0]?.workspaceId ?? "");
  const choices = openWorkspaceOf(props.workspace);
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void props.onSave(role === "off" ? null : role, role === "off" || workspace === "" ? [] : [workspace]);
  };
  return (
    <Dialog open={props.domain !== null} title={portalText("core", "account_admin.joining_from_b66156", { value0: props.domain?.domain ?? portalText("core", "account_admin.the_domain_c72917") })} onClose={props.onClose}>
      <form className="sec-dialog-form" onSubmit={submit}>
        <p className="sec-dialog-text">{portalText("core", "account_admin.people_with_an_address_on_this_domain_may_join_without_an_invitat_b00f44")}</p>
        <Field label={portalText("core", "account_admin.they_join_as_3c0da5")}>
          {(control) => (
            <Select {...control} value={role} onChange={(event) => setRole(event.target.value as AutoJoinRoleView | "off")}>
              <option value="off">{portalText("core", "account_admin.nobody_joins_without_an_invitation_806135")}</option>
              {AUTO_JOIN_ROLE.map((one) => (
                <option key={one} value={one}>
                  {WORKSPACE_ROLE_WORD[one]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {role === "off" ? null : <WorkspaceChoiceStatus read={props.workspace} />}
        {role === "off" || choices.length === 0 ? null : (
          <Field label={portalText("core", "account_admin.in_the_workspace_169ab3")}>
            {(control) => (
              <Select {...control} value={workspace} onChange={(event) => setWorkspace(event.target.value)}>
                <option value="">{portalText("core", "account_admin.choose_a_workspace_e2c819")}</option>
                {choices.map((one) => (
                  <option key={one.workspaceId} value={one.workspaceId}>
                    {one.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <div className="sec-dialog-actions">
          <Button variant="primary" type="submit" disabled={role !== "off" && workspace === ""}>{portalText("core", "account_admin.save_efc007")}</Button>
        </div>
      </form>
    </Dialog>
  );
}

// ── Allowed networks ──────────────────────────────────────────────────────────────────────────────────────────

function NetworkBlock(props: {
  readonly view: AccountSecurityView;
  readonly canWrite: boolean;
  readonly member: readonly AccountMemberView[];
  readonly accountId: string;
  readonly change: (act: () => Promise<PortalMessage>) => Promise<void>;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const [adding, setAdding] = useState(false);
  const nameOf = (personId: string): string => props.member.find((one) => one.personId === personId)?.displayName ?? portalText("core", "account_admin.someone_in_the_organization_26e7e1");
  const address = props.view.clientAddress;
  return (
    <Block
      title={portalText("core", "account_admin.allowed_networks_4ff64b")}
      dek={portalText("core", "account_admin.console_sign_in_only_api_keys_carry_their_own_network_rules_and_c_20cc73")}
      buttons={
        props.canWrite ? (
          <Button variant="secondary" onClick={() => setAdding(true)}>{portalText("core", "account_admin.add_a_range_542a4e")}</Button>
        ) : undefined
      }
    >
      <p className="sec-line">
        {address.address === null
          ? portalText("core", "account_admin.wetalk_cannot_see_the_address_of_this_browser_so_a_range_could_si_81a9ed")
          : portalText("core", "account_admin.wetalk_sees_this_browser_at_taken_from_our_own_proxy_never_from_a_bd4c12", { value0: address.address })}
        {address.insideAllowedNetwork === false ? portalText("core", "account_admin._that_address_is_outside_every_range_below_28a219") : ""}
      </p>
      {props.view.allowedNetwork.length === 0 ? (
        <BlockNote>{portalText("core", "account_admin.no_ranges_people_may_sign_in_to_the_console_from_any_network_28a625")}</BlockNote>
      ) : (
        <div className="op-table-wrap">
          <table className="table op-table">
            <thead>
              <tr>
                <th>{portalText("core", "account_admin.range_a16c5b")}</th>
                <th>{portalText("core", "account_admin.label_74341e")}</th>
                <th>{portalText("core", "account_admin.added_by_75a4b6")}</th>
                <th>
                  <span className="visually-hidden">{portalText("core", "account_admin.act_138a10")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {props.view.allowedNetwork.map((one) => (
                <tr key={one.accountAllowedNetworkId}>
                  <td className="op-cell op-cell-person">{one.network}</td>
                  <td className="op-cell">{one.label}</td>
                  <td className="op-cell">{nameOf(one.addedByPersonId)}</td>
                  <td className="op-cell op-cell-action">
                    {props.canWrite ? (
                      <button
                        type="button"
                        className="op-link"
                        onClick={() =>
                          void props.change(async () => {
                            await gateway.removeAccountAllowedNetwork(props.accountId, one.accountAllowedNetworkId);
                            return () => (portalText("core", "account_admin._was_removed_it_applies_from_the_next_request_d82caa", { value0: one.network }));
                          })
                        }
                      >{portalText("core", "account_admin.remove_e96390")}</button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AddNetworkDialog
        open={adding}
        onClose={() => setAdding(false)}
        onAdd={(range, label) =>
          props.change(async () => {
            await gateway.addAccountAllowedNetwork(props.accountId, range, label);
            setAdding(false);
            return () => (portalText("core", "account_admin.range_added_it_applies_from_the_next_request_0b6ed0"));
          })
        }
      />
    </Block>
  );
}

function AddNetworkDialog(props: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onAdd: (range: string, label: string) => Promise<void>;
}): ReactNode {
  usePortalTranslation("core");
  const [range, setRange] = useState("");
  const [label, setLabel] = useState("");
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (range.trim() === "" || label.trim() === "") return;
    void props.onAdd(range.trim(), label.trim()).then(() => {
      setRange("");
      setLabel("");
    });
  };
  return (
    <Dialog open={props.open} title={portalText("core", "account_admin.add_a_range_542a4e")} onClose={props.onClose}>
      <form className="sec-dialog-form" onSubmit={submit}>
        <p className="sec-dialog-text">{portalText("core", "account_admin.only_browsers_in_an_allowed_range_can_use_the_console_include_the_a78119")}</p>
        <Field label={portalText("core", "account_admin.range_a16c5b")}>
          {(control) => <Input {...control} value={range} placeholder="82.14.0.0/16" onChange={(event) => setRange(event.currentTarget.value)} />}
        </Field>
        <Field label={portalText("core", "account_admin.label_74341e")}>
          {(control) => <Input {...control} value={label} placeholder={portalText("core", "account_admin.restaurant_broadband_14707a")} onChange={(event) => setLabel(event.currentTarget.value)} />}
        </Field>
        <div className="sec-dialog-actions">
          <Button variant="primary" type="submit" disabled={range.trim() === "" || label.trim() === ""}>{portalText("core", "account_admin.add_61cc55")}</Button>
        </div>
      </form>
    </Dialog>
  );
}
